import { affectsCars, ROAD_LABEL } from "../../../shared/drive";
import { resortById, type ResortReport, type RoadEvent } from "../../../shared/mountains";
import { useHousehold } from "../../app/useHousehold";
import { useMountains } from "../../data/newsQueries";
import { dataStateOf } from "../../data/queries";
import { Icon } from "../../ui/Icon";
import { DirectionsButton } from "../../ui/Directions";
import { EmptyState } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import { SkyFoot } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { ResortLogo } from "./ResortLogo";
import { openSnowReport } from "./SnowReport";
import "./outdoors.css";

type MountainsConfig = Record<string, never>;

const inches = (n: number | null) => (n === null ? "—" : `${n}″`);
const drive = (m: number | null) => (m === null ? null : m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`);

/**
 * The family's ski mountains: new snow at each one's SNOTEL gauge, the NWS
 * snowfall forecast, the drive from home and the chain/traction laws and
 * closures on the way, with a powder-day call. Tap a mountain for the full
 * snow report (ours on top, the resort's page below) or for directions.
 */
function Mountains({ fullscreen, openFullscreen }: WidgetProps<MountainsConfig>) {
  const { settings, isAdmin } = useHousehold();
  useNow("minute");
  const q = useMountains();
  const state = dataStateOf(q, 60 * 60_000);
  if (settings.mountains.resorts.length === 0)
    return (
      <EmptyState
        icon="snow"
        title="Pick your mountains"
        detail="Choose up to six ski areas to see new snow, the drive and chain laws, and get a powder-day alert."
        action={
          isAdmin ? (
            <a className="btn btn-sm btn-primary" href="/admin#outdoors">
              Pick mountains
            </a>
          ) : undefined
        }
      />
    );
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="snow" title="Mountain conditions unavailable" detail={q.error?.message} />;
  const roads = data.roads.filter(affectsCars);
  const powder = data.resorts.filter((r) => r.powder);

  return (
    <div className="outdoor mtn">
      <WidgetHeader
        title="Mountains"
        meta={
          powder.length ? (
            <span className="mtn-powder-meta">
              ❄️ Powder {powder.some((r) => r.powder === "today") ? "day" : "tomorrow"}: {powder.map((r) => r.name).join(", ")}
            </span>
          ) : (
            `Powder day at ${data.powderInches}″+`
          )
        }
        onTitle={fullscreen ? undefined : openFullscreen}
      />
      <ul className="mtn-grid">
        {data.resorts.map((r) => (
          <ResortCard key={r.id} r={r} roads={roads.filter((e) => e.resorts.includes(r.id))} />
        ))}
      </ul>
      {roads.length > 0 && (
        <ul className="mtn-roads" aria-label="On the way">
          {roads.map((e) => (
            <li key={e.id} className={`mtn-road is-${e.kind}`}>
              <span className="mtn-road-kind">{ROAD_LABEL[e.kind]}</span>
              <strong>{e.route}</strong>
              <span className="mtn-road-where">{e.where ?? e.title}</span>
            </li>
          ))}
        </ul>
      )}
      <SkyFoot source="Snow: USDA NRCS SNOTEL · Forecast: NWS · Roads & drive: CDOT COtrip" stamp={data.stamp} state={state} updatedAt={q.dataUpdatedAt} />
    </div>
  );
}

function ResortCard({ r, roads }: { r: ResortReport; roads: RoadEvent[] }) {
  const closed = roads.some((e) => e.kind === "closure");
  const at = resortById(r.id);
  return (
    <li className={`mtn-card ${r.powder ? `is-powder-${r.powder}` : ""}`}>
      <button type="button" className="mtn-card-btn" onClick={() => openSnowReport(r.id)} aria-label={`${r.name}: open the snow report`}>
        <span className="mtn-name">
          <ResortLogo r={r} />
          {r.name}
          {r.powder && <span className="mtn-badge">{r.powder === "today" ? "Powder day" : "Powder tomorrow"}</span>}
        </span>
        <span className="mtn-new">
          <b>{inches(r.newSnow24In)}</b>
          <small>new in 24 h</small>
        </span>
        <span className="mtn-row" title={`Snow depth at the ${r.snotelName} gauge`}>
          48 h {inches(r.newSnow48In)} · depth {inches(r.depthIn)}
        </span>
        <span className="mtn-row">
          <Icon name="snow" size="0.95rem" /> {r.forecast24In === null ? "No forecast" : `${r.forecast24In}″ next 24 h · ${r.forecast48In ?? 0}″ 48 h`}
        </span>
        {(r.driveMin !== null || roads.length > 0) && (
          <span className={`mtn-row mtn-drive ${closed ? "warn-text" : ""}`}>
            <Icon name="car" size="0.95rem" /> {drive(r.driveMin) ?? "Drive"}
            {roads.length > 0 && <span className="mtn-laws">{closed ? "· road closed" : `· ${roads.some((e) => e.kind === "chain") ? "chain" : "traction"} law`}</span>}
          </span>
        )}
      </button>
      {at && <DirectionsButton target={{ name: r.name, lat: at.lat, lon: at.lon }} className="mtn-dir icon-btn" iconOnly />}
    </li>
  );
}

export const mountainsWidget: WidgetDefinition<MountainsConfig> = {
  type: "mountains",
  version: 1,
  name: "Mountains",
  description: "Your ski areas: new snow, the snowfall forecast, the drive and chain laws on the way, and powder-day calls.",
  icon: "snow",
  category: "weather",
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  stackHeight: 520,
  defaults: {},
  settings: [],
  provider: "nrcs-awdb · nws · cotrip",
  supportsFullscreen: true,
  Component: Mountains,
};
